import { ToQueryStringFilters, usePagination, useToQueryString } from '@/core'
import { Pagination } from '@/core/types'
import { createInjectionState, toReactive } from '@vueuse/core'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import type { TCustomer } from '../../../types'
import { useCatalogQuery } from '../query/useCatalogQuery'
import { useCustomerQuery } from '../query/useCustomerQuery'

const [useProvideCustomer, useCustomerModule] = createInjectionState(() => {
  const form = ref<TCustomer>({})
  const route = useRoute()
  const rfc = ref('')
  const postalCodefilters = ref<ToQueryStringFilters>({
    param: '',
  })
  const stateFilters = ref<ToQueryStringFilters>({
    param: '',
  })
  const cfdiFilters = ref<ToQueryStringFilters>({
    param: '',
  })
  const postalCodeQueryParams = useToQueryString(
    toReactive(postalCodefilters),
    {},
  )
  const stateQueryParams = useToQueryString(toReactive(stateFilters), {})
  const cfdiQueryParams = useToQueryString(toReactive(cfdiFilters), {})

  const customerQuery = useCustomerQuery({
    modelId: computed(() => route.params?.id?.toString()),
    enableQuery: {
      loadRecords: true,
      loadRecord: false,
    },
    rfc,
    handlers: {
      onLoadRecord: (reponse) => {
        form.value = reponse
      },
    },
  })

  const postalCodesPagination = usePagination()
  const statesPagination = usePagination()
  const cfdisPagination = usePagination()

  const catalogQuery = useCatalogQuery({
    pages: {
      postalCodesCatalog: postalCodesPagination.currentPage,
      statesCatalog: statesPagination.currentPage,
      cfdisCatalog: cfdisPagination.currentPage,
    },
    filters: {
      postalCodesCatalog: postalCodeQueryParams.queryParams,
      statesCatalog: stateQueryParams.queryParams,
      cfdisCatalog: cfdiQueryParams.queryParams,
    },
    enableQuery: {
      paymentFormsCatalog: true,
      paymentMethodsCatalog: true,
      cfdisCatalog: true,
      classificationsCatalog: true,
      fiscalRegimesCatalog: true,
      currenciesCatalog: true,
      countriesCatalog: true,
      statesCatalog: true,
      postalCodesCatalog: true,
    },
    handlers: {
      onCatalogChange: (pagination: Pagination, key: string) => {
        if (key === 'postalCodesCatalog') {
          postalCodesPagination.setPagination(pagination)
        }
        if (key === 'statesCatalog') {
          statesPagination.setPagination(pagination)
        }
        if (key === 'cfdisCatalog') {
          cfdisPagination.setPagination(pagination)
        }
      },
    },
  })

  const onAdd = async (payload: TCustomer) => {
    await customerQuery.addRecord.mutateAsync({
      ...payload,
      sale_id: route.params?.id.toString(),
    })
    await customerQuery.loadRecords.refetch()
  }

  const onEdit = async (payload: TCustomer) => {
    await customerQuery.editRecord.mutateAsync({
      ...payload,
      sale_id: route.params?.id.toString(),
    })
    await customerQuery.loadRecords.refetch()
  }

  const onDelete = async (payload: TCustomer) => {
    await customerQuery.deleteRecord.mutateAsync(payload)
    await customerQuery.loadRecords.refetch()
  }

  const onSearchCustomer = (payload: string) => {
    rfc.value = payload
    return customerQuery.loadRecord.refetch()
  }

  const model = computed(() => customerQuery.loadRecord.data.value)

  const tableData = computed(() => customerQuery.loadRecords.data?.value)

  const paymentMethodsCatalog = computed(
    () => catalogQuery.paymentMethodsCatalog.data?.value,
  )

  const paymentFormsCatalog = computed(
    () => catalogQuery.paymentFormsCatalog.data?.value,
  )

  const cfdisCatalog = computed(
    () => catalogQuery.cfdisCatalog.data?.value?.data,
  )

  const fiscalRegimesCatalog = computed(
    () => catalogQuery.fiscalRegimesCatalog.data?.value,
  )

  const classificationsCatalog = computed(
    () => catalogQuery.classificationsCatalog.data?.value,
  )

  const currenciesCatalog = computed(
    () => catalogQuery.currenciesCatalog.data.value,
  )

  const countriesCatalog = computed(
    () => catalogQuery.countriesCatalog.data?.value,
  )

  const statesCatalog = computed(
    () => catalogQuery.statesCatalog.data?.value?.data,
  )

  const postalCodesCatalog = computed(
    () => catalogQuery.postalCodesCatalog.data?.value?.data,
  )

  const isLoading = computed(
    () =>
      customerQuery.loadRecords.isLoading.value ||
      customerQuery.addRecord.isLoading.value ||
      customerQuery.editRecord.isLoading.value ||
      catalogQuery.paymentMethodsCatalog.isLoading.value ||
      catalogQuery.paymentFormsCatalog.isLoading.value ||
      catalogQuery.cfdisCatalog.isLoading.value ||
      catalogQuery.fiscalRegimesCatalog.isLoading.value ||
      catalogQuery.classificationsCatalog.isLoading.value ||
      catalogQuery.currenciesCatalog.isLoading.value ||
      catalogQuery.countriesCatalog.isLoading.value,
  )

  return {
    form,
    isLoading,
    tableData,
    paymentFormsCatalog,
    paymentMethodsCatalog,
    cfdisCatalog,
    fiscalRegimesCatalog,
    classificationsCatalog,
    currenciesCatalog,
    countriesCatalog,
    statesCatalog,
    postalCodesCatalog,
    model,
    postalCodesPagination: toReactive(postalCodesPagination),
    postalCodefilters,
    stateFilters,
    statesPagination: toReactive(statesPagination),
    cfdiFilters,
    cfdiPagination: toReactive(cfdisPagination),
    onEdit,
    onAdd,
    onDelete,
    onSearchCustomer,
  }
})

export { useCustomerModule, useProvideCustomer }
